import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { SiDataService } from './services/si-data.service';
import { Subject, BehaviorSubject } from 'rxjs';
import { SiAuthenticationService } from '@strategic-insights/ng-authentication';
import { CookieService } from 'ngx-cookie';
import { SiTabConfig } from './models/si-tab-config.model';
import { ISiTabConfig } from './interfaces/si-tab-config.interface';
declare let d3;

@Injectable({
  providedIn: 'root'
})
export class AppService {
  tokenExpireStatus: Subject<boolean> = new Subject<boolean>();
  appBlocker: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  appLoader: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  filterExpanded = new BehaviorSubject<boolean>(false);
  userPreferanceEnabled = new BehaviorSubject<boolean>(true);
  filterHovered = new BehaviorSubject<boolean>(false);
  tabHeaderConfigs: object;
  commonConstants: object;
  exportConfigs: object;
  kpiConfigs: object;
  chartConfig: object;
  fraudPolicyConfigs: object;
  timeURI: string = 'data/getRefreshTime';
  overlayTimeURI: string = 'data/getRefreshTimeOverlay';
  categoryTimeURI: string = 'data/getRefreshTimeCategory';
  private tabList: Array<string> = [];
  private tabMapping: Array<ISiTabConfig> = [];
  constructor(private siDataService: SiDataService,
    private siAuthenticationService: SiAuthenticationService,
    private cookieService: CookieService) { }

  expandFilterPanel(clickOnFilter) {
    this.filterExpanded.next(clickOnFilter);
  }

  enableUserPreferance(value) {
    this.userPreferanceEnabled.next(value);
  }

  hoverFilterPanel(hoverOnFilter) {
    this.filterHovered.next(hoverOnFilter);
  }

  setTabList(tabList) {
    this.tabList = tabList;
  }

  setTabMapping(tabList) {
    this.tabMapping = [];
    tabList.forEach(tab => {
      this.tabMapping.push(new SiTabConfig(tab.tabName, tab.tabId, tab.path, tab.tabSequence));
    });
  }

  getTabMapping() {
    const tabMapping = this.tabMapping.filter(tab => this.tabList.indexOf(tab.getTabId()) !== -1);
    return tabMapping.sort((a, b) => a.getTabSequence() - b.getTabSequence());
  }

  setActiveTab(activeTab) {
    this.cookieService.put('activeTab', activeTab);
  }
  getActiveTab() {
    return this.cookieService.get('activeTab');
  }

  isRealObject(value) {
    const objEmptyCheck = Object.keys(value).length === 0 && value.constructor === Object;
    return objEmptyCheck && value && value !== 'null' && value !== 'undefined';
  }

  isRealValue(value) {
    if (value.constructor === Array) {
      return value && value.length > 0;
    }
    return value && value !== 'null' && value !== 'undefined';
  }

  setTabHeaderConfig(config) {
    this.tabHeaderConfigs = config;
  }

  getTabHeaderConfig() {
    return this.tabHeaderConfigs;
  }

  setCommonConstants(config) {
    this.commonConstants = config;
  }

  getCommonConstants() {
    return this.commonConstants;
  }

  setExportsConfig(config) {
    this.exportConfigs = config;
  }

  getExportsConfig() {
    return this.exportConfigs;
  }

  setKpiConfig(config) {
    this.kpiConfigs = config;
  }

  getKpiConfig() {
    return this.kpiConfigs;
  }

  setChartConfig(config) {
    this.chartConfig = config;
  }

  getChartConfig() {
    return this.chartConfig;
  }

  getFileDownloadData(url): Observable<Array<object>> {
    return new Observable<Array<object>>((observer) => {
      this.siDataService.getData(url)
        .subscribe(
          (response) => {
            observer.next(response);
            observer.complete();
          }, (error) => {
            observer.error(error);
            observer.complete();
          });
    });
  }

  sortDates(dates) {
    dates.sort(function (firstDate, secondDate) {
      if (firstDate.date < secondDate.date) { // sort dates in accending order
        return -1;
      }
      if (firstDate.date > secondDate.date) {
        return 1;
      }
      return 0; // default return value (no sorting)
    });

    return dates;
  }
  formatTN(tn: string): string {
    //return (tn).substr(0, 3) + '-' + (tn).substr(3, 3) + '-' + (tn).substr(6, 4);
    if (tn.length > 10)
          return '('+(tn).substr(0,tn.length-10)+') '+(tn).substr(-10,3)+'-'+(tn).substr(-7,3)+'-'+(tn).substr(-4,4);
        else
          return (tn).substr(0, 3) + '-' + (tn).substr(3, 3) + '-' + (tn).substr(6, 4);
}

  formatBytes(bytes, decimals) {
    if (typeof bytes === 'string') {
      // tslint:disable:radix
      bytes = parseInt(bytes);
    }
    if (bytes === 0) { return '0 B'; }
    const base = 1024,
      dm = decimals <= 0 ? 0 : decimals || 2,
      sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
      i = Math.floor(Math.log(bytes) / Math.log(base));
    return parseFloat((bytes / Math.pow(base, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  shortenLargeNumber(num, digits) {
    num = d3.format('.2s')(num);
    switch (num[num.length - 1]) {
      case 'G': return num.slice(0, -1) + 'B';
      case 'k': return num.slice(0, -1) + 'K';
    }
    return num;
  }

  replaceFormatter(num, formatString) {
    num = d3.format(formatString)(num);
    switch (num[num.length - 1]) {
      case 'G': return num.slice(0, -1) + 'B';
      case 'k': return num.slice(0, -1) + 'K';
      case 'm': return (num.replace(/m/, '') / 1000).toFixed(2);
    }
    return num;
  }

  getActiveCustomerAndFormat() {
    let activeCustomer = this.siAuthenticationService.activeCustomer.getValue();
    return activeCustomer.replace(/^\w/, c => c.toUpperCase()); // First letter capital
  }

  sortDataBygivenFormat(data, key, order) {
    if (order === 'decending') {
      return data.sort((a, b) => (Number(a[key]) > Number(b[key])) ? 1 : ((Number(b[key]) > Number(a[key])) ? -1 : 0));
    }
    return data.sort((a, b) => (Number(a[key]) < Number(b[key])) ? 1 : ((Number(b[key]) < Number(a[key])) ? -1 : 0));
  }

  formateDateForUTC(date) {
    let formattedDate = date.slice(0, 10).split('-');
    formattedDate = formattedDate[1] + '/' + formattedDate[2] + '/' + formattedDate[0];
    return formattedDate;
  }

  setAppliedFiltersForXLSX(XLSXObj, filterCount, filtersArray) {
    // let filterCount = 6;
    // let XLSXObj: Array<object> = Object.assign({}, this.exportConfigGlobal['XLSXConfigQueryCount']['additionalInformation']);

    if (filtersArray['tollFree'] && filtersArray['tollFree']['isApplied']) {
      filterCount++;
      const rowspanCount = parseInt((filtersArray['tollFree']['formattedTn'].length / 34).toString()) + 1;
      XLSXObj['A' + filterCount] = {
        'data': 'Toll Free: ', 'rowspan': rowspanCount,
        'style': { 'bold': true, 'verticalAlignment': 'center', 'fontSize': 8, 'fontFamily': 'Arial' }
      };
      XLSXObj['B' + filterCount] = {
        'data': filtersArray['tollFree']['formattedTn'], 'colspan': 2,
        'rowspan': rowspanCount, 'style': { 'wrapText': true, 'fontSize': 8, 'fontFamily': 'Arial' }
      };
    }

    if (filtersArray['telephone'] && filtersArray['telephone']['isApplied']) {
      filterCount++;
      const rowspanCount = parseInt((filtersArray['telephone']['formattedTn'].length / 34).toString()) + 1;
      XLSXObj['A' + filterCount] = {
        'data': 'Telephone: ', 'rowspan': rowspanCount,
        'style': { 'bold': true, 'verticalAlignment': 'center', 'fontSize': 8, 'fontFamily': 'Arial' }
      };
      XLSXObj['B' + filterCount] = {
        'data': filtersArray['telephone']['formattedTn'],
        'colspan': 2, 'rowspan': rowspanCount, 'style': { 'wrapText': true, 'fontSize': 8, 'fontFamily': 'Arial' }
      };
    }

    if (filtersArray['customList'] && filtersArray['customList']['isApplied']) {
      filterCount++;
      const rowspanCount = parseInt((filtersArray['customList']['custom_list_one_file_name'].length / 34).toString()) + 1;
      XLSXObj['A' + filterCount] = {
        'data': 'Custom List Files: ', 'rowspan': rowspanCount,
        'style': { 'bold': true, 'verticalAlignment': 'center', 'fontSize': 8, 'fontFamily': 'Arial' }
      };
      XLSXObj['B' + filterCount] = {
        'data': filtersArray['customList']['custom_list_one_file_name'],
        'colspan': 2, 'rowspan': rowspanCount, 'style': { 'wrapText': true, 'fontSize': 8, 'fontFamily': 'Arial' }
      };
    }

    if (filtersArray['states'] && filtersArray['states']['isApplied']) {
      filterCount++;
      const rowspanCount = parseInt((filtersArray['states']['stateNames'].length / 34).toString()) + 1;
      XLSXObj['A' + filterCount] = {
        'data': 'States: ', 'rowspan': rowspanCount,
        'style': { 'bold': true, 'verticalAlignment': 'center', 'fontSize': 8, 'fontFamily': 'Arial' }
      };
      XLSXObj['B' + filterCount] = {
        'data': filtersArray['states']['stateNames'], 'colspan': 2,
        'rowspan': rowspanCount, 'style': { 'wrapText': true, 'fontSize': 8, 'fontFamily': 'Arial' }
      };
    }

    if (filtersArray['score'] && filtersArray['score']['isApplied'] && !filtersArray['score']['score']) {
      filterCount++;
      const rowspanCount = parseInt((filtersArray['score']['score'].length / 34).toString()) + 1;
      XLSXObj['A' + filterCount] = {
        'data': 'Score: ', 'rowspan': rowspanCount,
        'style': { 'bold': true, 'verticalAlignment': 'center', 'fontSize': 8, 'fontFamily': 'Arial' }
      };
      XLSXObj['B' + filterCount] = {
        'data': filtersArray['score']['carrier_score'],
        'colspan': 2, 'rowspan': rowspanCount, 'style': { 'wrapText': true, 'fontSize': 8, 'fontFamily': 'Arial' }
      };
    }

    if (filtersArray['fraudPolicyList'] && filtersArray['fraudPolicyList']['isApplied']) {
      filterCount++;
      const rowspanCount = parseInt((filtersArray['fraudPolicyList']['bucket'].length / 34).toString()) + 1;
      XLSXObj['A' + filterCount] = {
        'data': 'Fraud Policy: ', 'rowspan': rowspanCount,
        'style': { 'bold': true, 'verticalAlignment': 'center', 'fontSize': 8, 'fontFamily': 'Arial' }
      };
      XLSXObj['B' + filterCount] = {
        'data': filtersArray['fraudPolicyList']['bucket'], 'colspan': 2,
        'rowspan': rowspanCount, 'style': { 'wrapText': true, 'fontSize': 8, 'fontFamily': 'Arial' }
      };
    }

    if (filtersArray['categoryList'] && filtersArray['categoryList']['isApplied']) {
      filterCount++;
      const rowspanCount = parseInt((filtersArray['categoryList']['category_id'].length / 34).toString()) + 1;
      XLSXObj['A' + filterCount] = {
        'data': 'Category: ', 'rowspan': rowspanCount,
        'style': { 'bold': true, 'verticalAlignment': 'center', 'fontSize': 8, 'fontFamily': 'Arial' }
      };
      XLSXObj['B' + filterCount] = {
        'data': filtersArray['categoryList']['category_id'], 'colspan': 2,
        'rowspan': rowspanCount, 'style': { 'wrapText': true, 'fontSize': 8, 'fontFamily': 'Arial' }
      };
    }

    if (filtersArray['subCategoryList'] && filtersArray['subCategoryList']['isApplied']) {
      filterCount++;
      const rowspanCount = parseInt((filtersArray['subCategoryList']['sub_category_id'].length / 34).toString()) + 1;
      XLSXObj['A' + filterCount] = {
        'data': 'Sub Category: ', 'rowspan': rowspanCount,
        'style': { 'bold': true, 'verticalAlignment': 'center', 'fontSize': 8, 'fontFamily': 'Arial' }
      };
      XLSXObj['B' + filterCount] = {
        'data': filtersArray['subCategoryList']['sub_category_id'], 'colspan': 2,
        'rowspan': rowspanCount, 'style': { 'wrapText': true, 'fontSize': 8, 'fontFamily': 'Arial' }
      };
    }

    if ((filtersArray['bus_res'] && filtersArray['bus_res']['isApplied']) ||
      (filtersArray['invalid_tn'] && filtersArray['invalid_tn']['isApplied'])) {
      filterCount++;
      const rowspanCount = parseInt((filtersArray['bus_res']['selectedIndicatorsLabel'].length / 34).toString()) + 1;
      XLSXObj['A' + filterCount] = {
        'data': 'TN Indicators: ', 'rowspan': rowspanCount,
        'style': { 'bold': true, 'verticalAlignment': 'center', 'fontSize': 8, 'fontFamily': 'Arial' }
      };
      XLSXObj['B' + filterCount] = {
        'data': filtersArray['bus_res']['selectedIndicatorsLabel'], 'colspan': 2,
        'rowspan': rowspanCount, 'style': { 'wrapText': true, 'fontSize': 8, 'fontFamily': 'Arial' }
      };
    }

    if (filtersArray['ip_address'] && filtersArray['ip_address']['isApplied']) {
      filterCount++;
      const rowspanCount = parseInt((filtersArray['ip_address']['ip_address'].length / 34).toString()) + 1;
      XLSXObj['A' + filterCount] = {
        'data': 'IP Address: ', 'rowspan': rowspanCount,
        'style': { 'bold': true, 'verticalAlignment': 'center', 'fontSize': 8, 'fontFamily': 'Arial' }
      };
      XLSXObj['B' + filterCount] = {
        'data': filtersArray['ip_address']['ip_address'],
        'colspan': 2, 'rowspan': rowspanCount, 'style': { 'wrapText': true, 'fontSize': 8, 'fontFamily': 'Arial' }
      };
    }

    if (filtersArray['verstat_type'] && filtersArray['verstat_type']['isApplied']) {
      filterCount++;
      const rowspanCount = parseInt((filtersArray['verstat_type']['verstat_type'].length / 34).toString()) + 1;
      XLSXObj['A' + filterCount] = {
        'data': 'Verstat Type: ', 'rowspan': rowspanCount,
        'style': { 'bold': true, 'verticalAlignment': 'center', 'fontSize': 8, 'fontFamily': 'Arial' }
      };
      XLSXObj['B' + filterCount] = {
        'data': filtersArray['verstat_type']['verstat_type'],
        'colspan': 2, 'rowspan': rowspanCount, 'style': { 'wrapText': true, 'fontSize': 8, 'fontFamily': 'Arial' }
      };
    }

    if (filtersArray['signing_type'] && filtersArray['signing_type']['isApplied']) {
      filterCount++;
      const rowspanCount = parseInt((filtersArray['signing_type']['signing_type'].length / 34).toString()) + 1;
      XLSXObj['A' + filterCount] = {
        'data': 'Signing Type: ', 'rowspan': rowspanCount,
        'style': { 'bold': true, 'verticalAlignment': 'center', 'fontSize': 8, 'fontFamily': 'Arial' }
      };
      XLSXObj['B' + filterCount] = {
        'data': filtersArray['signing_type']['signing_type'],
        'colspan': 2, 'rowspan': rowspanCount, 'style': { 'wrapText': true, 'fontSize': 8, 'fontFamily': 'Arial' }
      };
    }

    if (filtersArray['origIdList'] && filtersArray['origIdList']['isApplied']) {
      filterCount++;
      const rowspanCount = parseInt((filtersArray['origIdList']['origId_list_one_file_name'].length / 34).toString()) + 1;
      XLSXObj['A' + filterCount] = {
        'data': 'ORIGID: ', 'rowspan': rowspanCount,
        'style': { 'bold': true, 'verticalAlignment': 'center', 'fontSize': 8, 'fontFamily': 'Arial' }
      };
      XLSXObj['B' + filterCount] = {
        'data': filtersArray['origIdList']['origId_list_one_file_name'],
        'colspan': 2, 'rowspan': rowspanCount, 'style': { 'wrapText': true, 'fontSize': 8, 'fontFamily': 'Arial' }
      };
    }

    if (filtersArray['clientID_type'] && filtersArray['clientID_type']['isApplied']) {
      filterCount++;
      const rowspanCount = parseInt((filtersArray['clientID_type']['clientID_type'].length / 34).toString()) + 1;
      XLSXObj['A' + filterCount] = {
        'data': 'Client ID: ', 'rowspan': rowspanCount,
        'style': { 'bold': true, 'verticalAlignment': 'center', 'fontSize': 8, 'fontFamily': 'Arial' }
      };
      XLSXObj['B' + filterCount] = {
        'data': filtersArray['clientID_type']['clientID_type'],
        'colspan': 2, 'rowspan': rowspanCount, 'style': { 'wrapText': true, 'fontSize': 8, 'fontFamily': 'Arial' }
      };
    }

    if (filtersArray['productid_type'] && filtersArray['productid_type']['isApplied']) {
      filterCount++;
      const rowspanCount = parseInt((filtersArray['productid_type']['productid_type'].length / 34).toString()) + 1;
      XLSXObj['A' + filterCount] = {
        'data': 'PRODUCT ID: ', 'rowspan': rowspanCount,
        'style': { 'bold': true, 'verticalAlignment': 'center', 'fontSize': 8, 'fontFamily': 'Arial' }
      };
      XLSXObj['B' + filterCount] = {
        'data': filtersArray['productid_type']['productid_type'],
        'colspan': 2, 'rowspan': rowspanCount, 'style': { 'wrapText': true, 'fontSize': 8, 'fontFamily': 'Arial' }
      };
    }

    if (filtersArray['ipAddressList'] && filtersArray['ipAddressList']['isApplied']) {
      filterCount++;
      const rowspanCount = parseInt((filtersArray['ipAddressList']['IpAddress_list_one_file_name'].length / 34).toString()) + 1;
      XLSXObj['A' + filterCount] = {
        'data': 'Ip Address: ', 'rowspan': rowspanCount,
        'style': { 'bold': true, 'verticalAlignment': 'center', 'fontSize': 8, 'fontFamily': 'Arial' }
      };
      XLSXObj['B' + filterCount] = {
        'data': filtersArray['ipAddressList']['IpAddress_list_one_file_name'],
        'colspan': 2, 'rowspan': rowspanCount, 'style': { 'wrapText': true, 'fontSize': 8, 'fontFamily': 'Arial' }
      };
    }

    if (filtersArray['attestation_type'] && filtersArray['attestation_type']['isApplied']) {
      filterCount++;
      const rowspanCount = parseInt((filtersArray['attestation_type']['attestation_type'].length / 34).toString()) + 1;
      XLSXObj['A' + filterCount] = {
        'data': 'Attestation Type: ', 'rowspan': rowspanCount,
        'style': { 'bold': true, 'verticalAlignment': 'center', 'fontSize': 8, 'fontFamily': 'Arial' }
      };
      XLSXObj['B' + filterCount] = {
        'data': filtersArray['attestation_type']['attestation_type'],
        'colspan': 2, 'rowspan': rowspanCount, 'style': { 'wrapText': true, 'fontSize': 8, 'fontFamily': 'Arial' }
      };
    }

    return XLSXObj;

  }

  addCustomerLogoInImageHeader(canvasContainer, imagePath) {
    const canvasContainerContext = canvasContainer.getContext('2d');
    const canvasWidth = canvasContainer.width;
    const canvasHeight = canvasContainer.height;
    let self = this;
    return new Promise(function (resolve, reject) {
      const headerImg = new Image;
      headerImg.src = imagePath;
      headerImg.onload = function () {
        const imageWidth = this['width'];
        const imageHeight = this['height'];
        if(imageHeight > 90){
          canvasContainerContext.drawImage(headerImg, (canvasWidth / 2 - imageWidth / 2), 15, imageWidth-100, imageHeight-(imageHeight*0.40));
        } else{
          canvasContainerContext.drawImage(headerImg, (canvasWidth / 2 - imageWidth / 2), 15, imageWidth, imageHeight);
        }
        resolve(canvasContainer);
      };
    });
  }

  /**
* Gets date list
* @returns date list
*/
  getDateList(tabName: string): Observable<Array<object>> {
    let urlParam = '';
    if (tabName === 'Overlay Report') {
      urlParam = this.overlayTimeURI;
    } else if (tabName === 'Category Report' || tabName === 'Category Distribution') {
      urlParam = this.categoryTimeURI;
    } else {
      urlParam = this.timeURI;
    }
    return new Observable<Array<object>>((observer) => {
      this.siDataService.getData(urlParam)
        .subscribe(
          (response) => {
            // if (response.length > 0) {
            //   response = this.getTransformedRefreshTime(response);
            // }
            observer.next(response);
            observer.complete();
          }, (error) => {
            observer.error(error);
            observer.complete();
          });
    });
  }
}
